import { useQuery } from "@apollo/client";
import classNames from "classnames";
import { FC, MouseEvent, useEffect, useMemo } from "react";
import Divider from "~/components/Divider";
import { PATH } from "~/constants/enum";
import { QUERY_GET_ACTUAL_PRODUCT } from "~/data/home";
import { Product } from "~/services/product";
import { getConfig, getUrlCustom, isUsingLayoutV2 } from "~/utils/theme";
import { transformProductItem } from "~/utils/transform";
import ProductImage, {
  Props as ProductImageProps,
} from "./SelectedProductImage";
import styles from "./ProductItem.module.scss";
import ProductName, { Props as ProductNameProps } from "./ProductName";
import ProductPrice from "./SelectedProductPrice";
import { isEmpty } from "lodash";
import { QUERY_PRODUCT_INDEX } from "~/data/product/gql";
import { VariantSelection } from "~/sections/BundleContent/BundleContent";

export type ProductItemSize =
  | "large"
  | "medium"
  | "small"
  | "xsmall"
  | "smaller";
export enum DisplayInfo {
  Price = "price",
  Rating = "rating",
  Wishlist = "wishlist",
  Compare = "compare",
  QuickView = "quickView",
  AddToCart = "addToCart",
  Title = "title",
}
export interface ProductItemProps {
  size?: ProductItemSize;
  data: Partial<Product>;
  search?: string;
  onProductClick?: (product: Partial<Product>) => void;
  params?: any;
  loading?: boolean;
  className?: string;
  rootClassName?: string;
  onRemove?: (data: Partial<Product>) => void;
  actions?: (data: Partial<Product>) => JSX.Element;
  type?: "vertical" | "horizontal";
  imageProps?: Partial<ProductImageProps>;
  nameProps?: Partial<ProductNameProps>;
  onCompleted?: (data: Product, clientOptions: any) => void;
  displayInfo?: DisplayInfo[] | any[];
  onChangeVariant?: (product: Partial<Product>) => void;
  selectedVariant?: VariantSelection;
  onChangeSelectedVariantQuantity?: (sku: string, quantity: number) => void;
}

const ProductItem: FC<ProductItemProps> = ({
  data: optimizeData,
  search,
  onProductClick,
  onRemove,
  params = {},
  loading: productItemLoading,
  className = "",
  actions,
  type = "vertical",
  imageProps,
  nameProps,
  onCompleted,
  displayInfo = [DisplayInfo.Title, DisplayInfo.Price],
  onChangeVariant,
  selectedVariant,
  onChangeSelectedVariantQuantity,
}) => {
  const themeConfig = getConfig("ProductItem");

  const { data: actualData, loading } = useQuery(QUERY_GET_ACTUAL_PRODUCT, {
    // nextFetchPolicy: "no-cache",
    onError: ({ message }) => {
      console.error(optimizeData.sku);
    },
    onCompleted(data) {
      onCompleted?.(data?.products?.items?.[0], {
        variables: { sku: optimizeData.sku },
      });
    },
    context: { fetchOptions: { method: "GET" } },
    variables: {
      sku: optimizeData.sku,
    },
    skip:
      !optimizeData?.sku || !isEmpty(optimizeData?.price_range?.minimum_price),
  });

  const productActual = transformProductItem(
    actualData?.products.items?.[0] || ({} as any)
  );

  const data: Partial<Product> = useMemo(() => {
    if (!optimizeData) return {};
    return {
      ...(optimizeData || {}),
      ...(productActual || {}),
      images: !!optimizeData?.images?.length
        ? optimizeData?.images
        : !!optimizeData?.media_gallery?.length
          ? optimizeData?.media_gallery?.map((item: any) => item.url)
          : !!productActual?.media_gallery?.length
            ? productActual?.media_gallery?.map((item: any) => item.url)
            : [optimizeData?.image?.url],
    };
  }, [optimizeData, productActual]);

  const { data: dataProductIndex } = useQuery(QUERY_PRODUCT_INDEX, {
    variables: {
      sku: data?.sku,
    },
    skip: !data?.sku,
  });

  const productIndex: {
    last_24h_view_count: number;
    last_24h_sale_count: number;
    last_24h_in_cart_count: number;
  } = useMemo(() => {
    return dataProductIndex?.productIndex;
  }, [dataProductIndex]);

  const skeletonLoading =
    productItemLoading ||
    (isEmpty(optimizeData?.price_range?.minimum_price) && loading);

  const handleRemove = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();

    onRemove?.(data);
  };
  const handleAction = (e: MouseEvent<HTMLDivElement>) => {
    actions?.(data);
  };
  const handleClickProduct = () => {
    onProductClick?.(data);
  };

  const openChangeVariantModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onChangeVariant?.(data);
  };

  return (
    <div
      className={classNames(
        "flex flex-col product-item gap-2 h-full !border-none !p-0",
        styles.product_item,
        className,
        {
          ["!flex-row"]: type === "horizontal",
        }
      )}
      key={data.slug}
      data-sku={data.sku}
      onClick={handleClickProduct}
    >
      <div
        className={classNames(
          styles.product_item_hover,
          " relative aspect-square h-full",
          {
            "max-w-40 lg:max-w-[50%] flex-1": type === "horizontal",
          },
          imageProps?.className || ""
        )}
      >
        <ProductImage
          data={data as Product}
          loading={skeletonLoading}
          onRemove={onRemove && handleRemove}
          isBestSeller={!!productIndex?.last_24h_sale_count}
          {...(imageProps || {})}
        />
      </div>
      <div
        className={classNames({
          "flex-1": type === "horizontal",
          hidden:
            !displayInfo.includes(DisplayInfo.Price) &&
            !displayInfo.includes(DisplayInfo.Title),
          "flex flex-col items-center": isUsingLayoutV2,
        })}
      >
        <ProductName
          loading={skeletonLoading}
          name={data?.name}
          search={search}
          hidden={!displayInfo.includes(DisplayInfo.Title)}
          type={type}
          {...(nameProps || {})}
        />
        {themeConfig.showDivider && <Divider dividerColor="#3E3E3E" />}
        <div className="flex items-center justify-between w-full my-2">
          <ProductPrice
            data={data}
            loading={skeletonLoading}
            hidden={!displayInfo.includes(DisplayInfo.Price)}
            type={type}
          />
          <div
            className="flex items-center border border-solid border-gray-300 rounded-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentQty = selectedVariant?.quantity || 1;
                if (currentQty > 1) {
                  onChangeSelectedVariantQuantity &&
                    onChangeSelectedVariantQuantity(
                      data.sku || "",
                      currentQty - 1
                    );
                }
              }}
              className="px-2 py-1 hover:bg-gray-100 transition-colors"
              disabled={!selectedVariant || selectedVariant.quantity <= 1}
            >
              -
            </button>
            <input
              type="number"
              className="w-12 text-center border-x border-solid border-gray-300 py-1 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              min={1}
              value={selectedVariant?.quantity || 1}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 1) {
                  onChangeSelectedVariantQuantity &&
                    onChangeSelectedVariantQuantity(data.sku || "", value);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                const currentQty = selectedVariant?.quantity || 1;
                onChangeSelectedVariantQuantity &&
                  onChangeSelectedVariantQuantity(
                    data.sku || "",
                    currentQty + 1
                  );
              }}
              className="px-2 py-1 hover:bg-gray-100 transition-colors"
            >
              +
            </button>
          </div>
        </div>
        {/* <Star value={data.rating_summary} size={16} /> */}
      </div>

      {actions && <div onClick={handleAction}>{actions(data)} </div>}

      <button
        onClick={openChangeVariantModal}
        className="py-2 border-[1px] border-solid border-[#C4C4C4] rounded-md hover:scale-105 transition-all"
      >
        Choose Options
      </button>

      {/* Display selected variant attributes */}
      {selectedVariant?.sku && (
        <div className="flex flex-col gap-1 mt-2 text-xs text-gray-600">
          <div className="flex flex-wrap gap-1">
            {data.variants
              ?.find((v: any) => v.product.sku === selectedVariant.sku)
              ?.attributes?.map((attr: any, index: number, allAttrs: any[]) => (
                <div
                  key={`${attr.code}-${index}`}
                  className="flex items-center"
                >
                  <span>
                    <b>{attr.label}</b>
                  </span>
                  {index < allAttrs.length - 1 && (
                    <span className="mx-1">|</span>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Display selected variant information */}
      {selectedVariant?.productNote &&
        selectedVariant.productNote.length > 0 && (
          <div className="flex flex-col gap- text-xs text-gray-600">
            <div className="flex flex-wrap gap-1">
              {selectedVariant.productNote
                .filter((note) => note.type !== "color")
                .map((note, index, filteredNotes) => (
                  <div
                    key={`${note.name}-${index}`}
                    className="flex items-center"
                  >
                    <span>
                      <b>{note.name}</b>: {note.value}
                    </span>
                    {index < filteredNotes.length - 1 && (
                      <span className="mx-1">|</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
    </div>
  );
};

export default ProductItem;
